import { STORAGE_TOKEN } from "../constants";
import { createBrowserHistory } from "history";
import React, { useMemo } from "react";
import { FormData, ICreateProfile } from "../@types";
import { refreshIdToken } from "./refreshToken";
import { redirectToAuth } from "./navigation";
import { getToken, setToken } from "./token";
import { COUNTRIES } from "../constants/countries";
import ReactCountryFlag, { ReactCountryFlagProps } from "react-country-flag";
import { CognitoUserSession } from "amazon-cognito-identity-js";

export const isMobileDevice = window.innerWidth < 1030;

const isTokenExpired = (token: string): boolean => {
  try {
    const [, payload] = token.split(".");
    const decodedPayload = JSON.parse(atob(payload));
    const expirationTime = decodedPayload.exp * 1000;
    return Date.now() > expirationTime;
  } catch (error) {
    return true;
  }
};

export const getAuthToken = async (): Promise<string | null> => {
  try {
    const token = getToken();
    if (!token) {
      redirectToAuth();
      return null;
    }
    if (isTokenExpired(token)) {
      const newToken = await refreshIdToken();
      if (newToken) {
        setToken(newToken);
        return newToken;
      }
      redirectToAuth();
      return null;
    }
    return token;
  } catch (error) {
    redirectToAuth();
    return null;
  }
};

export const isPasswordValid = (password: string) => {
  const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
  if (!pattern.test(password)) {
    return "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number";
  }
  return "";
};

/**
 * @param {string} min Minimum number to generate
 * @param {number} max Maximum number to generate
 * @returns {string}
 */
export const generateNumber = (min = 1, max = 9000000): string => {
  min = Math.ceil(min);
  max = Math.floor(max);
  const num = Math.floor(Math.random() * (max - min + 1)) + min;
  return num.toString().padStart(12, "0");
};

export const sortData = <T extends Object>(
  data: Array<T>,
  sortColumn: keyof T,
  sortDirection: "ASC" | "DESC",
): Array<T> => {
  return data.sort((a, b) => {
    if (a[sortColumn] < b[sortColumn]) {
      return sortDirection === "ASC" ? -1 : 1;
    }
    if (a[sortColumn] > b[sortColumn]) {
      return sortDirection === "ASC" ? 1 : -1;
    }
    return 0;
  });
};

export const copyToClipboard = async (data: string) => {
  await navigator.clipboard.writeText(data);
};

export const history = createBrowserHistory();

export const shortenFileName = (file: File, length = 15) => {
  const name = file.name;
  if (name.length > length) {
    const fileType = file.type.split("/")[1];
    const fileName = name.substring(0, length - 4);

    return fileName + "..." + fileType;
  }

  return name;
};

export const ValidateFormSubmit = (
  formData: FormData,
  setValidationResults: React.Dispatch<React.SetStateAction<FormData>>,
  setError: React.Dispatch<React.SetStateAction<string>>,
) => {
  const errors = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    createPassword: "",
    confirmPassword: "",
    businessName: "",
    country: "",
  };

  // Check for required fields
  if (!formData.businessName) {
    errors.businessName = "Business Name is required";
  }
  if (formData.businessName.length < 2) {
    errors.businessName = "Business Name should be atleast 2 characters.";
  }

  if (!formData.email) {
    errors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    errors.email = "Email is invalid";
  }

  // Password validation
  if (!formData.createPassword) {
    errors.createPassword = "Password is required";
  } else if (formData.createPassword.length < 8) {
    errors.createPassword = "Password must be at least 8 characters";
  } else if (!/[A-Z]/.test(formData.createPassword)) {
    errors.createPassword =
      "Password must contain at least one uppercase letter";
  } else if (!/[a-z]/.test(formData.createPassword)) {
    errors.createPassword =
      "Password must contain at least one lowercase letter";
  } else if (!/[0-9]/.test(formData.createPassword)) {
    errors.createPassword = "Password must contain at least one number";
  } else if (!/[^A-Za-z0-9]/.test(formData.createPassword)) {
    errors.createPassword =
      "Password must include at least one special character";
  }

  // Set validation results
  setValidationResults(errors);

  // Check for errors and set error state
  for (const key in errors) {
    if (errors.hasOwnProperty(key)) {
      if (errors[key as keyof typeof errors]) {
        setError(errors[key as keyof typeof errors]);
        return true; // Errors are found
      }
    }
  }

  return false; // No errors found
};

type barChartData = {
  [key: string]: {
    expense: number;
    invoice: number;
  };
};

export function shortenText(
  text: string | number | undefined | null,
  maxLength: number,
  ellipsis: string = "...",
): string {
  if (text === null || text === undefined) {
    return ""; // Handle undefined or null input gracefully
  }

  const textString = typeof text === "number" ? String(text) : text;

  if (textString.length <= maxLength) {
    return textString;
  }

  return textString.substring(0, maxLength - ellipsis.length) + ellipsis;
}

export const calculateChartHeight = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth >= 1280) {
    return 500;
  } else if (screenWidth >= 768) {
    return 700;
  } else {
    return 650;
  }
};

export const calculateAdjustedRate = (
  rate: number,
  exchangeRate?: number,
): number => {
  console.error("Invalid rate or exchange rate", { rate, exchangeRate });
  return exchangeRate ? rate / exchangeRate : rate;
};

const PAYMENT_CURRENCY_LIST = [
  {
    value: "NGN",
    label: "Naira ",
    name: "NGN ",
    icon: ReactCountryFlag({
      countryCode: "NG",
      svg: true,
      style: {
        width: "20px",
        height: "15px",
        marginRight: "10px",
        marginTop: "-5px",
      },
    }),
  },
  {
    value: "USD",
    label: "Dollar  ",
    name: "USD",
    icon: ReactCountryFlag({
      countryCode: "US",
      svg: true,
      style: {
        width: "20px",
        height: "15px",
        marginRight: "10px",
        marginTop: "-5px",
      },
    }),
  },
  {
    value: "GBP",
    label: "Pound Sterling ",
    name: "GBP",
    icon: ReactCountryFlag({
      countryCode: "GB",
      svg: true,
      style: {
        width: "20px",
        height: "15px",
        marginRight: "10px",
        marginTop: "-5px",
      },
    }),
  },
  {
    value: "CAD",
    label: "Canadian Dollar  ",
    name: "CAD",
    icon: ReactCountryFlag({
      countryCode: "CA",
      svg: true,
      style: {
        width: "20px",
        height: "15px",
        marginRight: "10px",
        marginTop: "-5px",
      },
    }),
  },
];

export const useGetPaymentCurrencyList = (user: Partial<ICreateProfile>) => {
  let country = user.country ?? "NG";
  if (country == "Nigeria") {
    country = "NG";
  }
  const userCurrencyCode: any = COUNTRIES.find(
    (countryData) => countryData.iso2 == country,
  );

  const currencyList = useMemo(() => {
    const userCurrencyObject = {
      value: userCurrencyCode?.currency,
      label: userCurrencyCode?.currency_name,
      name: userCurrencyCode?.currency,
      icon: ReactCountryFlag({
        countryCode: userCurrencyCode?.iso2,
        svg: true,
        style: {
          width: "20px",
          height: "15px",
          marginRight: "10px",
          marginTop: "-5px",
        },
      }),
    };
    const filteredList = PAYMENT_CURRENCY_LIST?.filter(
      (currency) =>
        currency.value !== "NGN" &&
        currency.value !== userCurrencyCode?.currency,
    );
    return [userCurrencyObject, ...filteredList];
  }, [user.country]);

  return { currencyList, userCurrencyCode };
};

export const checkTokenExpiration = (session: CognitoUserSession) => {
  if (!session?.isValid()) {
    console.warn("Session is not valid.");
    return;
  }

  const accessToken = session.getAccessToken();
  const idToken = session.getIdToken();
  const refreshToken = session.getRefreshToken();

  const now = Math.floor(Date.now() / 1000);

  const accessExp = accessToken.getExpiration();
  const idExp = idToken.getExpiration();

  const accessExpiresAt = new Date(accessExp * 1000);
  const idExpiresAt = new Date(idExp * 1000);

  const accessTimeRemaining = accessExp - now;
  const idTimeRemaining = idExp - now;

  console.log("🔐 Token Expiry Check:");
  console.log(
    `- Access Token expires in: ${accessTimeRemaining}s → ${accessExpiresAt.toLocaleString()}`,
  );
  console.log(
    `- ID Token expires in: ${idTimeRemaining}s → ${idExpiresAt.toLocaleString()}`,
  );

  if (refreshToken) {
    console.log(
      "🔁 Refresh Token is present (Note: AWS doesn't expose refresh expiration directly).",
    );
  } else {
    console.warn("⚠️ No refresh token found in session.");
  }
};
